import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Bot } from '../types/bot.types'
import { botService } from '../services/botService'
import { useWhatsApp } from '../hooks/useWhatsApp'
import { Loading } from '../components/common/Loading'
import QRCodeModal from '../components/bots/QRCodeModal'
import toast from 'react-hot-toast'
import {
  Bot as BotIcon,
  Wifi,
  WifiOff,
  QrCode,
  MessageSquare,
  Users,
  Send,
  RefreshCw
} from 'lucide-react'

const BotDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  
  const [bot, setBot] = useState<Bot | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showQRCode, setShowQRCode] = useState(false)
  const [messageText, setMessageText] = useState('')
  const [selectedContact, setSelectedContact] = useState('')
  const [showContacts, setShowContacts] = useState(false)
  const [showQRModal, setShowQRModal] = useState(false)

  // Hook do WhatsApp
  const {
    status,
    isConnected,
    qrCode,
    contacts,
    conversations,
    isLoading: whatsappLoading,
    error,
    disconnect,
    checkStatus,
    loadContacts,
    loadConversations,
    sendMessage
  } = useWhatsApp(Number(id))

  // Carregar dados do bot
  useEffect(() => {
    const loadBot = async () => {
      try {
        setIsLoading(true)
        const result = await botService.getBot(Number(id))
        if (result.data && result.data.success && result.data.bot) {
          setBot(result.data.bot)
        } else {
          toast.error('Erro ao carregar bot')
          navigate('/bots')
        }
      } catch (error) {
        toast.error('Erro ao carregar bot')
        navigate('/bots')
      } finally {
        setIsLoading(false)
      }
    }

    if (id) {
      loadBot()
    }
  }, [id, navigate])

  // Carregar dados do WhatsApp quando conectado
  useEffect(() => {
    if (isConnected) {
      loadContacts()
      loadConversations()
    }
  }, [isConnected, loadContacts, loadConversations])

  const handleConnect = async () => {
    setShowQRModal(true)
  }

  const handleDisconnect = async () => {
    await disconnect()
    setShowQRCode(false)
  }

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedContact) {
      toast.error('Preencha a mensagem e selecione um contato')
      return
    }

    const result = await sendMessage({
      to: selectedContact,
      message: messageText
    })

    if (result.success) {
      setMessageText('')
      toast.success('Mensagem enviada!')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'text-green-600 bg-green-100'
      case 'authenticating':
        return 'text-yellow-600 bg-yellow-100'
      case 'failed':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'connected':
        return 'Conectado'
      case 'authenticating':
        return 'Autenticando...'
      case 'failed':
        return 'Falhou'
      default:
        return 'Desconectado'
    }
  }

  if (isLoading) {
    return <Loading fullScreen />
  }

  if (!bot) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Bot não encontrado</h1>
          <button
            onClick={() => navigate('/bots')}
            className="mt-4 btn btn-primary"
          >
            Voltar para Bots
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <BotIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{bot.session_name}</h1>
            <p className="text-gray-600">Gerenciar bot WhatsApp</p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status)}`}>
            {getStatusText(status)}
          </span>
          <button
            onClick={() => navigate('/bots')}
            className="btn btn-md btn-outline"
          >
            Voltar
          </button>
        </div>
      </div>

      {/* Status e Controles */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Card de Status */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title flex items-center">
              {isConnected ? <Wifi className="h-5 w-5 text-green-600 mr-2" /> : <WifiOff className="h-5 w-5 text-gray-400 mr-2" />}
              Status da Conexão
            </h3>
          </div>
          <div className="card-content">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(status)}`}>
                  {getStatusText(status)}
                </span>
              </div>
              
              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                  {error}
                </div>
              )}

              <div className="flex space-x-2">
                {!isConnected ? (
                  <button
                    onClick={handleConnect}
                    disabled={whatsappLoading || status === 'authenticating'}
                    className="btn btn-primary btn-sm flex-1"
                  >
                    {whatsappLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Wifi className="h-4 w-4 mr-2" />}
                    Conectar
                  </button>
                ) : (
                  <button
                    onClick={handleDisconnect}
                    disabled={whatsappLoading}
                    className="btn btn-error btn-sm flex-1"
                  >
                    <WifiOff className="h-4 w-4 mr-2" />
                    Desconectar
                  </button>
                )}
                
                <button
                  onClick={checkStatus}
                  disabled={whatsappLoading}
                  className="btn btn-outline btn-sm"
                >
                  <RefreshCw className={`h-4 w-4 ${whatsappLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* QR Code */}
        {status === 'authenticating' && qrCode && (
          <div className="card lg:col-span-2">
            <div className="card-header">
              <h3 className="card-title flex items-center">
                <QrCode className="h-5 w-5 text-blue-600 mr-2" />
                QR Code para Conectar
              </h3>
            </div>
            <div className="card-content">
              <div className="flex flex-col items-center space-y-4">
                <div className="bg-white p-4 rounded-lg border">
                  <img 
                    src={`data:image/png;base64,${qrCode}`} 
                    alt="QR Code" 
                    className="w-64 h-64"
                  />
                </div>
                <p className="text-sm text-gray-600 text-center">
                  Escaneie o QR Code com seu WhatsApp para conectar o bot
                </p>
                <button
                  onClick={() => setShowQRCode(!showQRCode)}
                  className="btn btn-outline btn-sm"
                >
                  {showQRCode ? 'Ocultar' : 'Mostrar'} QR Code
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Funcionalidades quando conectado */}
      {isConnected && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Enviar Mensagem */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title flex items-center">
                <Send className="h-5 w-5 text-green-600 mr-2" />
                Enviar Mensagem
              </h3>
            </div>
            <div className="card-content">
              <div className="space-y-4">
                <div>
                  <label className="label">Contato</label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={selectedContact}
                      onChange={(e) => setSelectedContact(e.target.value)}
                      placeholder="Número do WhatsApp (ex: 5511999999999)"
                      className="input flex-1"
                    />
                    <button
                      onClick={() => setShowContacts(!showContacts)}
                      className="btn btn-md btn-outline"
                    >
                      <Users className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="label">Mensagem</label>
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Digite sua mensagem..."
                    className="input min-h-[100px] resize-none"
                    rows={4}
                  />
                </div>
                
                <button
                  onClick={handleSendMessage}
                  disabled={whatsappLoading || !messageText.trim() || !selectedContact}
                  className="btn btn-primary w-full"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Enviar Mensagem
                </button>
              </div>
            </div>
          </div>

          {/* Contatos */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title flex items-center">
                <Users className="h-5 w-5 text-blue-600 mr-2" />
                Contatos ({contacts.length})
              </h3>
            </div>
            <div className="card-content">
              <div className="max-h-96 overflow-y-auto">
                {contacts.length > 0 ? (
                  <div className="space-y-2">
                    {contacts.slice(0, 10).map((contact) => (
                      <div
                        key={contact.id}
                        className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"
                        onClick={() => setSelectedContact(contact.number)}
                      >
                        <div>
                          <p className="font-medium text-sm">
                            {contact.name || contact.number}
                          </p>
                          <p className="text-xs text-gray-500">
                            {contact.number}
                          </p>
                        </div>
                        {contact.isGroup && (
                          <span className="badge badge-primary">Grupo</span>
                        )}
                      </div>
                    ))}
                    {contacts.length > 10 && (
                      <p className="text-sm text-gray-500 text-center">
                        E mais {contacts.length - 10} contatos...
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Nenhum contato encontrado</p>
                    <button
                      onClick={loadContacts}
                      className="btn btn-outline btn-sm mt-2"
                    >
                      Carregar Contatos
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conversas */}
      {isConnected && conversations.length > 0 && (
        <div className="card mt-6">
          <div className="card-header">
            <h3 className="card-title flex items-center">
              <MessageSquare className="h-5 w-5 text-purple-600 mr-2" />
              Conversas Recentes ({conversations.length})
            </h3>
          </div>
          <div className="card-content">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {conversations.slice(0, 6).map((chat) => (
                <div
                  key={chat.id}
                  className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedContact(chat.id)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-sm truncate">
                      {chat.name}
                    </h4>
                    {chat.unreadCount > 0 && (
                      <span className="badge badge-primary">
                        {chat.unreadCount}
                      </span>
                    )}
                  </div>
                  {chat.lastMessage && (
                    <p className="text-xs text-gray-500 truncate">
                      {chat.lastMessage.body}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {chat.isGroup ? 'Grupo' : 'Individual'}
                  </p>
                </div>
              ))}
            </div>
          </div>
          </div>
        )}

        {/* QR Code Modal */}
        {bot && (
          <QRCodeModal
            isOpen={showQRModal}
            onClose={() => setShowQRModal(false)}
            botId={bot.id}
            botName={bot.session_name}
          />
        )}
      </div>
    )
  }

  export default BotDetail